/** Dashboard / Acasă (App-Dashboard.dc.html) — 4 solduri, „În lucru acum" live, campanii, notificări. */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBalance, useCampaigns, useClips, useNotifications } from '../api/hooks';
import { useSession } from '../context/SessionContext';
import { counterLabel } from '../lib/format';
import { CLIP_STATUS_LABEL, ClipStatusBadge, isProcessing } from '../components/clip';
import { Skeleton } from '../components/ui';
import { timeAgo } from '../lib/format';
import type { Clip } from '../api/types';

const COUNTERS = ['organic_premium', 'organic_ultra', 'reclame_premium', 'reclame_ultra'] as const;
const TAGS: Record<string, string> = {
  organic_premium: 'AI ~10 min',
  organic_ultra: 'editor 12–48h',
  reclame_premium: 'AI ~10 min',
  reclame_ultra: 'editor 12–48h',
};

export function DashboardPage() {
  const { user } = useSession();
  const balance = useBalance();
  const clips = useClips();
  const campaigns = useCampaigns('activa');
  const notifs = useNotifications();

  useEffect(() => {
    document.title = 'Dashboard — Reclame Video';
  }, []);

  const counters = balance.data?.counters;
  const totalSold = counters ? Object.values(counters).reduce((a, b) => a + b, 0) : 0;
  const isFirst = balance.isSuccess && totalSold === 0 && (clips.data?.total ?? 0) === 0;

  const working = (clips.data?.items ?? []).filter((c) => isProcessing(c.status)).slice(0, 4);

  const today = new Intl.DateTimeFormat('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
  const firstName = user.name?.split(' ')[0] || user.email.split('@')[0];

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', textTransform: 'capitalize' }}>{today}</p>
          <h1 className="bricolage" style={{ margin: '6px 0 0', fontWeight: 800, fontSize: 'clamp(26px,3.4vw,34px)' }}>
            Salut, {firstName}
          </h1>
          {isFirst ? (
            <p style={{ margin: '8px 0 0', fontSize: 14.5, color: 'var(--muted)' }}>
              Bine ai venit! Contul tău e pregătit — mai lipsește doar primul pachet de clipuri.
            </p>
          ) : (
            <p style={{ margin: '8px 0 0', fontSize: 14.5, color: 'var(--muted)' }}>
              Ai <strong style={{ color: 'var(--ink)' }}>{totalSold} clipuri</strong> în sold, gata de folosit. Un clip nou e gata în ~10 minute.
            </p>
          )}
        </div>
        {!isFirst && (
          <Link to="/creeaza" className="btn btn-primary" style={{ padding: '15px 24px', fontSize: 16 }}>
            <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>+</span>
            Creează clip
          </Link>
        )}
      </div>

      {isFirst && (
        <div
          className="rv-in"
          data-testid="dashboard-firstrun"
          style={{ marginTop: 28, border: '1.5px dashed var(--line)', borderRadius: 20, padding: '56px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center' }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--ink)', color: 'var(--bg)', padding: '9px 13px', borderRadius: 999, fontSize: 12, letterSpacing: '.14em', fontWeight: 800 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'rv-pulse 2s infinite' }} />
            REC
          </span>
          <p className="bricolage" style={{ margin: 0, fontWeight: 800, fontSize: 22 }}>Sold: 0 clipuri</p>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--muted)', maxWidth: '44ch', lineHeight: 1.65 }}>
            Alege un pachet — clipurile intră în sold imediat, nu expiră niciodată, iar primul clip e gata în ~10 minute.
          </p>
          <Link to="/cumpara" className="btn btn-primary" style={{ marginTop: 6, padding: '15px 26px', fontSize: 15.5 }}>
            Cumpără primul pachet
          </Link>
        </div>
      )}

      {!isFirst && (
        <>
          <section aria-label="Soldul tău de clipuri" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginTop: 24 }}>
            {balance.isLoading
              ? COUNTERS.map((c) => <Skeleton key={c} height={110} />)
              : COUNTERS.map((c) => {
                  const n = counters?.[c] ?? 0;
                  const empty = n === 0;
                  return (
                    <div key={c} style={{ background: 'var(--surface)', border: `1px solid ${empty ? 'var(--warn)' : 'var(--line)'}`, borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: c.startsWith('reclame') ? 'var(--accent)' : 'var(--ok)' }} />
                        <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--muted)' }}>{counterLabel(c)}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', background: 'var(--bg2)', color: 'var(--muted)', borderRadius: 999, padding: '3px 8px' }}>{TAGS[c]}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span className="bricolage tnum" data-testid={`dash-sold-${c}`} style={{ fontWeight: 800, fontSize: 32, color: empty ? 'var(--warn)' : 'var(--ink)' }}>{n}</span>
                        <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{n === 1 ? 'clip' : 'clipuri'}</span>
                      </div>
                      {empty && (
                        <Link to="/cumpara" style={{ fontSize: 12.5, fontWeight: 800, textDecoration: 'none' }}>
                          Adaugă clipuri →
                        </Link>
                      )}
                    </div>
                  );
                })}
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 24, marginTop: 34, alignItems: 'start' }} className="dash-grid">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 34, minWidth: 0 }}>
              <section aria-label="Clipuri în lucru">
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                  <h2 className="bricolage" style={{ fontWeight: 800, fontSize: 20 }}>În lucru acum</h2>
                  <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>se actualizează live</span>
                </div>
                {working.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
                      {working.map((w) => (
                        <WorkingRow key={w.id} clip={w} />
                      ))}
                    </div>
                    <p style={{ margin: '10px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>
                      Poți închide pagina liniștit — primești notificare pe email când clipul e gata.
                    </p>
                  </>
                ) : (
                  <div style={{ marginTop: 14, border: '1.5px dashed var(--line)', borderRadius: 16, padding: '36px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
                    <p style={{ margin: 0, fontWeight: 800, fontSize: 16 }}>Nimic în lucru acum</p>
                    <p style={{ margin: 0, fontSize: 13.5, color: 'var(--muted)', maxWidth: '38ch' }}>
                      Primul clip e gata în ~10 minute: alegi un șablon sau descrii produsul, iar AI-ul face restul.
                    </p>
                    <Link to="/creeaza" className="btn btn-primary btn-sm" style={{ padding: '12px 20px', fontSize: 14.5 }}>
                      Creează primul clip
                    </Link>
                  </div>
                )}
              </section>

              <section aria-label="Campanii recente">
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                  <h2 className="bricolage" style={{ fontWeight: 800, fontSize: 20 }}>Campaniile tale</h2>
                  <Link to="/campanii" style={{ fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>Vezi toate →</Link>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 12, marginTop: 14 }}>
                  {(campaigns.data?.items ?? []).slice(0, 3).map((c) => (
                    <Link key={c.id} to="/campanii" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 11, textDecoration: 'none', color: 'var(--ink)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 800, fontSize: 15 }}>{c.name}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 800, background: 'var(--bg2)', border: '1px solid var(--line)', borderRadius: 999, padding: '3px 8px', color: 'var(--muted)' }}>
                          {c.category === 'reclame' ? 'Reclame' : 'Organic'}
                        </span>
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{c.clipCount ?? 0} clipuri</span>
                    </Link>
                  ))}
                  <Link to="/campanii" style={{ border: '1.5px dashed var(--line)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, textDecoration: 'none', color: 'var(--muted)', minHeight: 120 }}>
                    <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: '50%', border: '1.5px dashed currentColor', display: 'grid', placeItems: 'center', fontSize: 18, fontWeight: 800 }}>+</span>
                    <span style={{ fontWeight: 800, fontSize: 13.5 }}>Campanie nouă</span>
                    <span style={{ fontSize: 12, textAlign: 'center' }}>grupează clipurile cu același produs și ton</span>
                  </Link>
                </div>
              </section>
            </div>

            <aside aria-label="Notificări" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h2 className="bricolage" style={{ fontWeight: 800, fontSize: 17 }}>Notificări</h2>
                {(notifs.data?.unread ?? 0) > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 800, background: 'var(--accent)', color: 'var(--accent-ink)', borderRadius: 999, padding: '3px 8px' }}>
                    {notifs.data?.unread} noi
                  </span>
                )}
              </div>
              {(notifs.data?.items ?? []).slice(0, 5).map((n) => (
                <div key={n.id} style={{ display: 'flex', gap: 11, padding: '11px 2px', borderTop: '1px solid var(--line)' }}>
                  <span aria-hidden="true" style={{ flex: 'none', marginTop: 5, width: 8, height: 8, borderRadius: '50%', background: n.read ? 'var(--line)' : 'var(--accent)' }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.45, fontWeight: n.read ? 600 : 800 }}>{n.title}</p>
                    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{timeAgo(n.createdAt)}</span>
                  </div>
                </div>
              ))}
              {(notifs.data?.items ?? []).length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--muted)', padding: '10px 2px' }}>Nicio notificare încă.</p>
              )}
              <Link to="/notificari" style={{ marginTop: 6, fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>
                Vezi toate notificările →
              </Link>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

function WorkingRow({ clip }: { clip: Clip }) {
  const pct = clip.progress ?? 0;
  const eta = Math.max(1, Math.round((100 - pct) / 12));
  return (
    <Link to={`/clipuri/${clip.id}`} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '14px 16px', display: 'flex', gap: 14, alignItems: 'center', textDecoration: 'none', color: 'var(--ink)' }}>
      <span aria-hidden="true" style={{ flex: 'none', width: 40, height: 68, borderRadius: 8, border: '1px solid var(--line)', backgroundColor: 'var(--bg2)', backgroundImage: 'repeating-linear-gradient(135deg,rgba(120,105,90,.16) 0 7px,rgba(120,105,90,0) 7px 15px),linear-gradient(180deg,rgba(226,122,60,.3),rgba(0,0,0,.08))' }} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 800, fontSize: 14.5 }}>{clip.title || 'Clip fără titlu'}</span>
          <ClipStatusBadge status={clip.status} />
        </div>
        <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{CLIP_STATUS_LABEL[clip.status]}</span>
        <div style={{ height: 5, borderRadius: 3, background: 'var(--bg2)', overflow: 'hidden' }}>
          <span style={{ display: 'block', height: '100%', borderRadius: 3, background: 'var(--accent)', width: `${pct}%`, transition: 'width 1.1s ease' }} />
        </div>
      </div>
      <div style={{ flex: 'none', textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span className="bricolage tnum" style={{ fontWeight: 800, fontSize: 17 }}>{pct}%</span>
        <span className="tnum" style={{ fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap' }}>~{eta} min</span>
      </div>
    </Link>
  );
}
